import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import ProductMosaic from "./ProductMosaic";
import DiscoveryCard from "./DiscoveryCard";
import DiscoveryCategoryTile from "./DiscoveryCategoryTile";
import DiscoveryCardRow from "./DiscoveryCardRow";
import ShopSafelyPanel from "./ShopSafelyPanel";
import { fetchCategoryTree } from "../../api/categories";
import { productQueryKey, productQueryOptions } from "../../api/products";
import { fetchSpotlight, spotlightQueryKey } from "../../api/spotlight";
import { fetchGreetingRotation, greetingRotationQueryKey, saveGreetingRotation } from "../../api/greetingRotation";
import { getVisitorId, getStoredConsent } from "../../utils/consent";
import {
  batchKeyFor, readBatch, saveBatch, reconcileBatch, excludeStages,
  adoptGuestRotation, rotationKeyFor,
} from "../../utils/discoveryRotation";
import { contextSlugsFor, pickFallbackCategories, EXPLORE_ALL_PATH } from "../../utils/discoveryCategories";
import { useAuth } from "../../contexts/AuthContext";
import { useProductViewer } from "../../hooks/useProductViewer";
import { getSessionGreeting, greetingParts, firstNameFrom, fitGreetingSize, GREETING_MIN_PX } from "./discoveryGreetings";
import "./DiscoveryPanel.css";

// Frontend half of "apply the limit server-side as well as in the
// frontend" — defensive only. The backend's own SPOTLIGHT_MAX_PRODUCTS
// (backend/app/main.py) is the authoritative cap; this just guarantees
// the UI is still correct even if that response ever regressed.
const SPOTLIGHT_MAX_PRODUCTS = 3;

// How often an open tab notices it has crossed into the next period. A
// minute is far finer than the five-a-day schedule and costs nothing —
// it only compares two strings.
const PERIOD_CHECK_MS = 60_000;

// Server order is kept as-is (no re-ranking) — this only drops entries
// that can't render as a real card (no id/name) and repeats: the same id
// twice, or two colour/size variants of one item (shared variant_group,
// see CLAUDE.md "Product Variants"). Never pads a short list.
const uniqueRecommendations = (products) => {
  const seenIds = new Set();
  const seenGroups = new Set();
  const out = [];
  for (const p of Array.isArray(products) ? products : []) {
    if (out.length >= SPOTLIGHT_MAX_PRODUCTS) break;
    if (!p || p.id == null || typeof p.name !== "string" || !p.name.trim()) continue;
    if (seenIds.has(p.id) || (p.variant_group && seenGroups.has(p.variant_group))) continue;
    seenIds.add(p.id);
    if (p.variant_group) seenGroups.add(p.variant_group);
    out.push(p);
  }
  return out;
};

// One request per card, for the product itself: /spotlight returns only
// ids' worth of detail, and this is the exact per-account query the product
// page reads (productQueryOptions), so opening the card finds it already
// warm. Cards no longer show a rating, but this query is NOT redundant —
// it is where the card's own data comes from, and it is what makes the
// product page open instantly.
const detailQuery = (id, viewer) => ({
  ...productQueryOptions(id, viewer),
  // A 404/403 won't change on retry (same call as ProductDetailPage makes);
  // only a transient failure gets one more try.
  retry: (failures, error) => !error?.notFound && !error?.ageRestricted && failures < 1,
});

const sameIds = (a, b) => Array.isArray(a) && Array.isArray(b) && a.join(",") === b.join(",");

const SkeletonPick = () => (
  <div className="discovery-skeleton-card" aria-hidden="true" data-testid="discovery-skeleton-card">
    <div className="discovery-skeleton-media" />
  </div>
);

// Mounted only when a greeting is actually shown, so the guest view never
// uses up a slot in the rotation. The lazy initializer +
// getSessionGreeting's own session pin keep it fixed across rerenders,
// remounts and route changes (discoveryGreetings.js). Emoji are decoration,
// so they're hidden from screen readers. `remote` is the account's
// server-side rotation (cross-device); a draw saves back. `pool` is "all"
// only when the row really is personalized — see the heading slot below.
export const GreetingHeading = ({ firstName, userKey, remote, onPersist, pool = "all" }) => {
  const [greeting] = useState(() => getSessionGreeting({ userKey, remote, onPersist, pool }));
  const headingRef = useRef(null);
  // One call for both the fitting effect's dependency and the render, so
  // the string that gets measured and the string that gets painted can
  // never drift apart.
  const parts = greetingParts(greeting.text, firstName);
  const text = parts.map((part) => part.text).join("");

  // Scale this greeting down until it sits on one line. Measured rather
  // than guessed from character count: the same 40 characters are a
  // different width in every column, and the panel's column changes with
  // the viewport. Runs before paint, so no visible resize.
  useLayoutEffect(() => {
    const el = headingRef.current;
    if (!el) return undefined;

    const apply = () => {
      // Always from a clean slate: a previous pass's size or wrap must not
      // be what this one measures (and once wrapped, text no longer
      // overflows, so a latched wrap could never undo itself).
      el.classList.remove("discovery-greeting--wrap");
      el.style.removeProperty("font-size");
      const available = el.clientWidth;
      if (!available) return;
      const maxPx = parseFloat(getComputedStyle(el).fontSize);
      const fit = fitGreetingSize({ maxPx, availablePx: available, measuredPx: el.scrollWidth });
      if (!fit) return;                                   // already one line
      el.style.fontSize = `${fit.size}px`;

      // The estimate assumes width scales linearly with size, which
      // rounding and hinting make only approximately true — step down until
      // it really fits rather than giving up and wrapping.
      let size = fit.size;
      for (let i = 0; i < 8 && size > GREETING_MIN_PX && el.scrollWidth > el.clientWidth + 1; i++) {
        size = Math.max(GREETING_MIN_PX, Math.floor((size - 0.4) * 10) / 10);
        el.style.fontSize = `${size}px`;
      }
      // Only at the floor, with the line still too long, does it wrap —
      // better two readable lines than one unreadable one, and never clipped.
      if (el.scrollWidth > el.clientWidth + 1) el.classList.add("discovery-greeting--wrap");
    };

    apply();
    // Web fonts land after first paint and change every measurement, so
    // re-fit once they do.
    let cancelled = false;
    if (document.fonts?.ready) document.fonts.ready.then(() => { if (!cancelled) apply(); }).catch(() => {});
    // fonts.ready is already settled on a remount later in the session, so
    // it can miss the first-ever load of the greeting's italic face. Asking
    // for that face specifically covers it (a no-op once cached). The
    // greeting is nowrap inside a section that clips, so a late swap would
    // otherwise be cut at the section edge rather than merely overhang.
    document.fonts?.load?.('italic 700 1rem "Cormorant Garamond"')?.then(() => { if (!cancelled) apply(); })?.catch(() => {});

    // The parent, not the heading: its width is what constrains the text,
    // and observing the heading would re-fire on its own height changes.
    const parent = el.parentElement;
    if (!parent || typeof ResizeObserver === "undefined") return () => { cancelled = true; };
    const observer = new ResizeObserver(apply);
    observer.observe(parent);
    return () => { cancelled = true; observer.disconnect(); };
  }, [text]);

  return (
    // The inner span is load-bearing, not decoration: .discovery-greeting is
    // a flex container, so each child <span> would become its own flex item
    // and a child text run that is only whitespace is then not rendered at
    // all — which would close up the space between the name and a trailing
    // emoji ("Harold☀️"). One inline wrapper makes all of this ordinary
    // inline flow, where that space behaves normally.
    // `part.name` is tested before `part.emoji`: a name part carries no
    // `emoji` key, so the other order would silently drop the span.
    <h2 ref={headingRef} className={`discovery-greeting${greeting.fresh ? " discovery-greeting--reveal" : ""}`}>
      <span className="discovery-greeting-text">
        {parts.map((part, i) =>
          part.name ? <span key={i} className="discovery-greeting-name">{part.text}</span>
            : part.emoji ? <span key={i} aria-hidden="true">{part.text}</span>
              : part.text
        )}
      </span>
    </h2>
  );
};

/**
 * Homepage discovery/recommendation panel — one shared responsive
 * component with two state variants (logged-in / logged-out).
 *
 * The three recommendations rotate on a schedule, not on clicks: one
 * batch per period (morning / midday / afternoon / evening / night),
 * drawn once and then held for the rest of that period across reloads,
 * rerenders, route changes and other tabs. Opening a card is plain
 * navigation — it never retires a product.
 */
const DiscoveryPanel = () => {
  const { user, token, loading: authLoading } = useAuth();
  const accountId = user?.id ?? null;
  const queryClient = useQueryClient();

  // Which period we're in. Re-checked on a timer, and immediately whenever
  // the tab is looked at again, so a page left open over a boundary
  // rotates instead of showing the morning's picks all evening.
  const [batchKey, setBatchKey] = useState(() => batchKeyFor());
  useEffect(() => {
    const check = () => {
      const current = batchKeyFor();
      setBatchKey((previous) => (previous === current ? previous : current));
    };
    const timer = setInterval(check, PERIOD_CHECK_MS);
    window.addEventListener("focus", check);
    document.addEventListener("visibilitychange", check);
    return () => {
      clearInterval(timer);
      window.removeEventListener("focus", check);
      document.removeEventListener("visibilitychange", check);
    };
  }, []);

  // The ids fixed for this period, or null when it hasn't been drawn yet.
  const [batch, setBatch] = useState(null);
  // A draw that legitimately came back with nothing: the catalogue has no
  // eligible product for this shopper. Not stored — an empty period is
  // worth retrying on the next visit, but not on every rerender of this one.
  const [drewEmpty, setDrewEmpty] = useState(false);
  // Which exclude list the draw is using: the week's history, then just
  // today's, then none (src/utils/discoveryRotation.js). Bounded — a short
  // response steps forward once, it never loops.
  const [stage, setStage] = useState(0);
  const adoptedFor = useRef(null);
  useEffect(() => {
    // Signing in inherits the guest history from this browser, so the
    // account doesn't re-show what the same person just saw.
    if (accountId != null && adoptedFor.current !== accountId) {
      adoptedFor.current = accountId;
      adoptGuestRotation(accountId);
    }
    setBatch(readBatch(accountId, batchKey));
    setStage(0);
    setDrewEmpty(false);
  }, [accountId, batchKey]);

  // Another tab drawing the same period at the same moment: both settle on
  // whichever batch was written first, rather than each keeping its own.
  useEffect(() => {
    const storageKey = rotationKeyFor(accountId);
    const onStorage = (event) => {
      if (event.key != null && event.key !== storageKey) return;
      setBatch((mine) => {
        const agreed = reconcileBatch(accountId, batchKey, mine ?? []);
        if (!agreed.length) return mine;
        return sameIds(agreed, mine) ? mine : agreed;
      });
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [accountId, batchKey]);

  // Resolved once auth settles: null id means storage is blocked, which is
  // reported as "couldn't load" rather than an endless skeleton.
  const [visitor, setVisitor] = useState({ resolved: false, id: null });
  useEffect(() => {
    if (authLoading || !user) { setVisitor({ resolved: false, id: null }); return; }
    setVisitor({ resolved: true, id: getVisitorId() });
  }, [authLoading, user]);

  const needsDraw = batch === null && !drewEmpty;
  const stages = useMemo(
    () => excludeStages(accountId),
    // Recomputed for a new account or period, and once a draw is stored
    // (the history it reads has changed).
    [accountId, batchKey, needsDraw]                          // eslint-disable-line react-hooks/exhaustive-deps
  );
  const excludedIds = stages[Math.min(stage, stages.length - 1)] ?? [];

  // Logged-out visitors never fetch at all — no personalization implied, no
  // network call that could be read as "your behaviour was collected". Only
  // a period without a batch draws; the rest of the period reads storage.
  const spotlightQuery = useQuery({
    queryKey: spotlightQueryKey(visitor.id, accountId, excludedIds, batchKey),
    queryFn: () => fetchSpotlight({ visitorId: visitor.id, token, excludedIds }),
    enabled: needsDraw && !authLoading && !!user && !!visitor.id,
    retry: false,
    staleTime: Infinity,   // one draw per period, whatever else remounts
    gcTime: 0,
  });

  // Whether the row may draw a greeting that says the picks were chosen for
  // this shopper, straight from the draw. A stored batch was drawn in an
  // earlier page load, so there's no response to read it from; the recorded
  // consent decision stands in, since declining personalisation is what
  // makes the backend answer personalized: false. "No decision recorded" is
  // not a decline, so it resolves to true — the picks may well have been
  // personalised, and the only thing that turns on this is which pool of
  // greetings is allowed, never whether a greeting appears.
  const drawnPersonalized = spotlightQuery.data?.personalized;
  const personalizedRef = useRef(null);
  if (typeof drawnPersonalized === "boolean") personalizedRef.current = drawnPersonalized;
  const consentDecision = getStoredConsent();
  const personalized = personalizedRef.current ?? !(consentDecision && !consentDecision.personalisation);

  // A draw fixes the period's batch. Fewer than three back means the
  // exclude list was too strict for the catalogue: step to the next stage
  // once, then take what there is.
  useEffect(() => {
    if (!needsDraw || !spotlightQuery.isSuccess) return;
    const drawn = uniqueRecommendations(spotlightQuery.data?.products);
    if (drawn.length < SPOTLIGHT_MAX_PRODUCTS && stage < stages.length - 1) {
      setStage(stage + 1);
      return;
    }
    if (!drawn.length) { setDrewEmpty(true); return; }   // category tiles show
    // Only product ids are recorded — a category tile is presentation, not
    // something the shopper was shown and shouldn't see again.
    setBatch(saveBatch(accountId, batchKey, drawn.map((p) => p.id)));
  }, [needsDraw, spotlightQuery.isSuccess, spotlightQuery.data, stage, stages.length, accountId, batchKey]);

  // The batch is ids; the cards come from the product-detail endpoint, so
  // a reload renders the same three without a second draw.
  const viewer = useProductViewer();
  const batchIds = useMemo(() => (batch ?? []).slice(0, SPOTLIGHT_MAX_PRODUCTS), [batch]);
  const detailQueries = useQueries({ queries: batchIds.map((id) => detailQuery(id, viewer)) });
  const detailsSettled = detailQueries.every((q) => !q.isPending);

  // One value that changes exactly when a card's data does, so the two
  // memos below don't rerun on every render of the page.
  const detailStamp = detailQueries.map((q) => `${q.dataUpdatedAt}:${q.errorUpdatedAt}`).join("|");

  // The product page is reached by slug, so seed its key with what we
  // already have — otherwise opening a card refetches the same product.
  useEffect(() => {
    detailQueries.forEach((q) => {
      const slug = q.data?.slug;
      if (!slug) return;
      const key = productQueryKey(slug, viewer.userId ?? null);
      if (!queryClient.getQueryData(key)) queryClient.setQueryData(key, q.data);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailStamp, viewer.userId, queryClient]);

  // A product deleted, deactivated or age-gated since the batch was drawn
  // simply drops out — never a broken card, never a substitute nobody
  // chose. Each card keeps its own query, so a dropped one can't shift
  // another card's data onto the wrong product.
  const cards = useMemo(() => {
    const hydrated = [];
    batchIds.forEach((id, i) => {
      const query = detailQueries[i];
      const data = query?.data;
      if (!data || data.id == null) return;
      hydrated.push({ product: data });
    });
    return uniqueRecommendations(hydrated.map((c) => c.product))
      .map((product) => hydrated.find((c) => c.product.id === product.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchIds, detailStamp]);

  const productsSettled = !authLoading && !!user && !!visitor.id && !needsDraw && detailsSettled;
  const short = productsSettled && cards.length < SPOTLIGHT_MAX_PRODUCTS;

  // Only asked for when the catalogue came up short. The same cached tree
  // Shop by Category reads (cachedFetch), so on a normal page this costs
  // nothing; a failure here just means no tiles, never a broken row.
  const categoriesQuery = useQuery({
    queryKey: ["discovery-fallback-categories"],
    queryFn: () => fetchCategoryTree(false),
    enabled: short,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
  const categoriesPending = short && categoriesQuery.isPending;

  // Real categories, leaning towards what this row is already showing.
  // Derived from the shown products alone — no stored choice and nothing
  // from the draw response, so the same period renders the same tiles on
  // every reload.
  const tiles = useMemo(
    () => pickFallbackCategories(categoriesQuery.data, {
      needed: SPOTLIGHT_MAX_PRODUCTS - cards.length,
      context: contextSlugsFor(cards.map((c) => c.product)),
    }),
    [categoriesQuery.data, cards]
  );

  const fetchState = authLoading || !user
    ? "idle"
    : visitor.resolved && !visitor.id
      ? "unavailable"
      : spotlightQuery.isError && batch === null
        ? "error"
        : needsDraw || !detailsSettled || categoriesPending
          ? "loading"
          : "ready";

  // Both halves of "couldn't load": a failed request, and a visitor id we
  // could never read. Retrying re-reads the id (storage may have been
  // unblocked since) and asks again.
  const retry = useCallback(() => {
    if (!authLoading && user) setVisitor({ resolved: true, id: getVisitorId() });
    setStage(0);
    setDrewEmpty(false);
    spotlightQuery.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user, spotlightQuery.refetch]);

  // The account's cross-device greeting rotation — fetched alongside
  // /spotlight, bounded to 1.5s (src/api/greetingRotation.js), and entirely
  // optional: on any failure the browser's own copy is used.
  const rotationEnabled = !authLoading && !!user && !!token;
  const rotationQuery = useQuery({
    queryKey: greetingRotationQueryKey(user?.id),
    queryFn: () => fetchGreetingRotation(token),
    enabled: rotationEnabled,
    retry: false,
    staleTime: Infinity, // read once per page; the session's greeting is pinned anyway
  });
  const rotationPending = rotationEnabled && rotationQuery.isPending;

  // ── Logged-out variant ────────────────────────────────────────────
  if (!authLoading && !user) {
    return (
      <section className="editorial-outer">
        <div className="editorial-section discovery-panel discovery-panel--guest">
          <ProductMosaic />
          <div className="editorial-body discovery-body">
            <h2 className="discovery-heading">Shop safely with Emporia</h2>
            <p className="discovery-sub">Pay through Emporia checkout. The seller is paid only after delivery is confirmed.</p>
            <ShopSafelyPanel />
          </div>
        </div>
      </section>
    );
  }

  // ── Logged-in variant (also covers the brief auth-resolving window,
  // rendered with the panel's own loading state so nothing flashes) ──
  // The heading keeps a reserved-height placeholder until the response has
  // settled, so text never swaps in place — neither nameless → named
  // greeting, nor a greeting redrawn because the row turned out not to be
  // personalized after all.
  const couldNotLoad = fetchState === "error" || fetchState === "unavailable";
  // Only labels the row (see rowLabel below) — it used to swap the heading
  // for "Popular right now". A non-personalized row now gets a greeting
  // too, drawn from the pool of lines that claim nothing about where the
  // picks came from, so the distinction lives in the row's own accessible
  // label rather than in a heading that replaces the greeting.
  const showingPopular = fetchState === "ready" && personalized === false;
  // Every settled state now shows a greeting, so there is no longer a
  // branch that can skip the rotation.
  const waitingOnRotation = rotationPending;
  const loading = fetchState === "loading" || fetchState === "idle" || waitingOnRotation;
  // One row, products first, category tiles filling whatever is left.
  const showRow = fetchState === "ready" && (cards.length > 0 || tiles.length > 0) && !waitingOnRotation;
  // Nothing to recommend and no category to offer either — the panel still
  // has to lead somewhere.
  const showExplore = fetchState === "ready" && cards.length === 0 && tiles.length === 0 && !waitingOnRotation;
  const settled = !authLoading && !loading;

  const rowLabel = cards.length === 0
    ? "Categories to explore"
    : tiles.length > 0
      ? `${showingPopular ? "Popular products" : "Recommended products"} and categories to explore`
      : showingPopular ? "Popular products" : "Recommended products";

  let heading;
  if (!settled) {
    heading = <h2 className="discovery-greeting" aria-hidden="true" />;
  } else {
    heading = (
      // Keyed by the period: a tab left open past a period boundary draws
      // that period's greeting instead of keeping the previous one, at the
      // same moment the product batch rotates (the key changes when
      // batchKey does, which the timer / focus check above keeps current).
      // `pool` is what keeps the greeting honest: only a genuinely
      // personalized row may draw a line that says the picks were chosen
      // for this shopper.
      <GreetingHeading
        key={batchKey}
        pool={personalized ? "all" : "everyone"}
        firstName={firstNameFrom(user?.name)}
        userKey={user?.id}
        remote={rotationQuery.data}
        onPersist={(rotation) => saveGreetingRotation(token, rotation)}
      />
    );
  }

  return (
    <section className="editorial-outer">
      <div className="editorial-section discovery-panel">
        <ProductMosaic />
        <div className="editorial-body discovery-body">
          {heading}

          {/* Same wrapper as the loaded row (DiscoveryCardRow), so the row's
              width is set in one place and the cards can't resize as the
              skeleton is replaced. */}
          {loading && (
            <div className="discovery-cards-wrap">
              <div className="discovery-cards" role="progressbar" aria-busy="true" aria-label="Loading recommendations">
                {Array.from({ length: SPOTLIGHT_MAX_PRODUCTS }, (_, i) => <SkeletonPick key={i} />)}
              </div>
            </div>
          )}

          {/* A service failure, kept distinct from an exhausted catalogue:
              this one says what went wrong and offers the way back. */}
          {couldNotLoad && (
            <div className="discovery-fallback" data-testid="discovery-error">
              <p className="discovery-status-message" role="status">Couldn't load recommendations right now.</p>
              <div className="discovery-actions">
                <button type="button" className="discovery-action" onClick={retry}>Try again</button>
                <Link to={EXPLORE_ALL_PATH} className="discovery-action discovery-action--quiet">Explore products</Link>
              </div>
            </div>
          )}

          {/* Nothing eligible and no category artwork to offer — never an
              empty column. */}
          {showExplore && (
            <div className="discovery-fallback" data-testid="discovery-explore">
              <p className="discovery-fallback-title">Discover something new</p>
              <div className="discovery-actions">
                <Link to={EXPLORE_ALL_PATH} className="discovery-action">Explore products</Link>
              </div>
            </div>
          )}

          {showRow && (
            <DiscoveryCardRow label={rowLabel}>
              {cards.map(({ product }) => (
                <DiscoveryCard key={product.id} product={product} />
              ))}
              {tiles.map((category) => (
                <DiscoveryCategoryTile key={category.slug} category={category} />
              ))}
            </DiscoveryCardRow>
          )}
        </div>
      </div>
    </section>
  );
};

export default DiscoveryPanel;
